import React, { useState, useEffect } from 'react';
import SEO from '../components/SEO';
import PageBanner from '../components/PageBanner';
import Reveal from '../components/ui/Reveal';
import usePage from '../hooks/usePage';
import API, { UPLOADS_URL } from '../api/axios';

const FALLBACK_FACILITIES = [
  { id: 3, title: 'Junior Classrooms', description: 'Vibrant, colourful, and ergonomically designed classrooms tailored specifically for early learners with interactive digital learning tools.', icon: 'fa-chalkboard-user', image: 'facilities/Junior-Classroom.jpg' },
  { id: 4, title: 'Computer Lab (AI & IT Lab)', description: 'High-tech computer lab equipped with latest generation workstations, high-speed fiber internet, and cutting-edge software for coding and AI.', icon: 'fa-desktop', image: 'facilities/Computer-Lab.jpg' },
  { id: 5, title: 'Composite Science Lab', description: 'Modern, fully equipped laboratories for Physics, Chemistry, and Biology designed to foster practical inquiry and scientific experimentation.', icon: 'fa-flask', image: 'facilities/Science-Lab.jpg' },
  { id: 6, title: 'Music & Performing Arts Studio', description: 'Acoustically engineered music and performing arts studio equipped with instruments and creative practice spaces.', icon: 'fa-music', image: 'facilities/Music-Room.jpg' },
  { id: 7, title: 'Resource-Rich Library', description: 'An expansive knowledge repository housing thousands of curated volumes, international journals, encyclopedias, and digital research stations.', icon: 'fa-book-open', image: 'facilities/Library.jpg' },
  { id: 8, title: 'Campus Infrastructure', description: 'World-class architectural campus spanning sprawling green grounds, well-ventilated smart corridors, and CCTV security.', icon: 'fa-school', image: 'facilities/Infrastructure.jpg' },
  { id: 9, title: 'Multi-Purpose Auditorium', description: 'A grand, high-capacity theatrical auditorium equipped with professional acoustic sound and ambient stage lighting.', icon: 'fa-landmark', image: 'facilities/Auditorium.jpg' },
  { id: 10, title: 'Swimming Pool', description: 'Half-Olympic size, temperature-regulated, hygienic swimming pool maintained with continuous filtration systems and certified coaches.', icon: 'fa-person-swimming', image: 'facilities/Swimming-Pool.jpg' },
  { id: 11, title: 'Sports Complex & Athletic Arena', description: 'Sprawling athletic facilities featuring standard football turf, professional cricket practice pitches, basketball courts, and athletics track.', icon: 'fa-futbol', image: 'facilities/Sports-Complex.jpg' },
  { id: 12, title: 'Taekwondo & Martial Arts Dojo', description: 'Dedicated martial arts training dojo featuring safety matted flooring where students learn self-defense and discipline.', icon: 'fa-hand-fist', image: 'facilities/Taekwondo.jpg' },
  { id: 13, title: 'Safe GPS Transport Fleet', description: 'A modern fleet of air-conditioned school buses equipped with real-time GPS tracking, speed governors, CCTV cameras, and female attendants.', icon: 'fa-bus', image: 'facilities/Transport.jpg' },
  { id: 14, title: 'Health & Medical Infirmary', description: 'On-campus healthcare center staffed by a qualified full-time nurse with clean sanitized recovery beds and emergency first-aid equipment.', icon: 'fa-heart-pulse', image: 'facilities/Infirmary.jpg' },
];

const resolveImage = (src, fallback = '') => {
  if (!src) return fallback;
  if (src.startsWith('http://') || src.startsWith('https://') || src.startsWith('/')) return src;
  return `${UPLOADS_URL}/${src}`;
};

const stripHtml = (html) => {
  if (!html) return '';
  return String(html).replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
};

const renderParagraphs = (raw, fallback = '') => {
  const text = raw || fallback;
  const parts = String(text).replace(/\r/g, '').split(/\n\s*\n|\n/).map(s => s.trim()).filter(Boolean);
  return parts.map((p, i) => <p key={i} className="mb-4 text-muted leading-relaxed text-[15px]">{p}</p>);
};

const InfrastructurePage = () => {
  const pageData = usePage('infrastructure');
  const sections = pageData.sections || {};

  const banner = sections.banner || pageData.banner || {};
  const bannerTitle = banner.title || pageData.title || "Campus Infrastructure";
  const bannerSubtitle = banner.subtitle || "A Thoughtfully Planned Campus Fostering Academic, Creative and Athletic Excellence";
  const bannerBreadcrumb = banner.breadcrumb || "INFRASTRUCTURE";
  const bannerImgSrc = pageData.bannerImage || (pageData.banner_image ? resolveImage(pageData.banner_image) : null) || '/images/page-banners/curricullam-infrastructure.jpg';

  const intro = sections.intro || "St. Andrews World School, Sonipat offers a thoughtfully planned campus that balances academic spaces, recreational areas, and creative zones. Every facility has been planned to support the physical, intellectual, and artistic growth of our students while ensuring safety, hygiene, and comfort.";

  const [facilities, setFacilities] = useState([]);

  useEffect(() => {
    API.get('/facilities/public')
      .then((res) => {
        if (res.data?.data && Array.isArray(res.data.data) && res.data.data.length > 0) {
          setFacilities(res.data.data);
        }
      })
      .catch((err) => {
        console.error('Failed to load facilities list for infrastructure page:', err);
      });
  }, []);

  const displayedFacilities = facilities.length > 0 ? facilities : FALLBACK_FACILITIES;

  return (
    <>
      <SEO slug="infrastructure" title={pageData.title || "Campus Infrastructure"}
        description="Explore the state-of-the-art infrastructure of St. Andrews World School, Sonipat — smart classrooms, labs, studios, and sports arenas."
        keywords="Campus Infrastructure, School Grounds, Smart Classrooms, Sonipat Campus, SAWS Sonipat"
      />

      <PageBanner
        title={bannerTitle}
        subtitle={bannerSubtitle}
        breadcrumb={bannerBreadcrumb}
        bgImage={bannerImgSrc}
        fallback="/images/page-banners/curricullam-infrastructure.jpg"
      />

      {/* CAMPUS FACILITIES & SPACES */}
      <section className="section bg-surface-alt">
        <div className="container">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-12">
              <div className="kicker">Campus Spaces & Facilities</div>
              <h2 className="h-section mt-2">World-Class Campus Infrastructure</h2>
              <div className="w-16 h-1 bg-primary mx-auto mt-4 rounded-full" />
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {displayedFacilities.map((facility, idx) => {
              const fImg = resolveImage(facility.image, '/images/page-banners/curricullam-infrastructure.jpg');
              const desc = stripHtml(facility.description || facility.desc);
              return (
                <Reveal key={facility.id || idx} delayMs={idx * 50}>
                  <div className="card h-full flex flex-col overflow-hidden hover:shadow-xl transition-all duration-300 border border-gray-100 group hover:-translate-y-1 bg-white">
                    <div className="relative aspect-[16/10] overflow-hidden bg-gray-100">
                      <img
                        src={fImg}
                        alt={facility.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = '/images/page-banners/curricullam-infrastructure.jpg';
                        }}
                      />
                      <span className="absolute top-3 right-3 bg-black/60 backdrop-blur text-white text-[10px] font-bold px-2.5 py-1 rounded-full">
                        Facility {idx + 1}
                      </span>
                      {facility.icon && (
                        <div className="absolute top-3 left-3 w-8 h-8 rounded-lg bg-white/95 backdrop-blur text-primary flex items-center justify-center shadow-md">
                          <i className={`fas ${facility.icon} text-xs`} />
                        </div>
                      )}
                    </div>

                    <div className="p-6 flex-1 flex flex-col justify-between">
                      <div>
                        <h3 className="font-serif font-bold text-lg text-primary mb-2 group-hover:text-accent transition-colors">
                          {facility.title}
                        </h3>
                        <p className="text-muted text-xs leading-relaxed line-clamp-3">
                          {desc}
                        </p>
                      </div>
                      <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between text-[11px] font-semibold text-accent">
                        <span>Campus Facility</span>
                        <i className="fas fa-check-circle text-xs" />
                      </div>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
};

export default InfrastructurePage;
